/**
 * Newsletter.jsx — EVORA Newsletter Subscription Page
 *
 * FULL AUDIT FIXES vs submitted version:
 *
 * LAYOUT / FULL-WIDTH
 * ① max-width: 900px → removed from shell, widened to 1200px.
 *   Form content max-width properly scoped to inner element (560px).
 *   Shell is wide for benefits grid; form is appropriately narrow.
 * ② contain: layout style → removed in CSS (style breaks custom props).
 *
 * FORM / CONVERSION
 * ③ canSubmit disables button when email is empty/invalid.
 *   Previous: button was disabled on !canSubmit but the disabled prop
 *   value was correct. However — UX problem: the button appears greyed
 *   out by default on page load before the user does anything, which
 *   signals "broken". Fix: show enabled button, validate on submit.
 *   canSubmit kept for aria-disabled pattern (not HTML disabled on idle).
 * ④ inflightRef pattern: correct. Kept as-is.
 * ⑤ emailRef pattern: correct. Kept as-is.
 * ⑥ handleChange clears error on keystroke: correct. Kept.
 * ⑦ friendlyError: covers 409 (already subscribed) — this is the most
 *   important case for a newsletter form. Kept and documented.
 * ⑧ noValidate on form: correct — we handle validation ourselves,
 *   preventing inconsistent native browser UI.
 * ⑨ source: 'newsletter-page' in API body: excellent — enables analytics
 *   segmentation by acquisition source.
 *
 * JSX STRUCTURE
 * ⑩ Benefits: generic <IconCheck> used for all 4 benefit icons.
 *   Different icons per benefit card lifts scannability and perceived
 *   quality. Each gets its own contextual icon (inline SVG, no import).
 * ⑪ FAQ: .nl-faq__answer-inner wrapper added — required for the CSS grid
 *   row animation trick (answer needs overflow:hidden inner container).
 * ⑫ FAQ: details/summary pattern correct. Kept. Native browser accordion
 *   is accessible by default — keyboard, screen reader, no JS needed.
 * ⑬ Success state: animates via CSS (nl-fadeup in CSS).
 * ⑭ JSON-LD: @type 'WebPage' correct for this page type.
 *   SubscribeAction schema added to mainEntity for richer structured data.
 * ⑮ og:image:width / og:image:height added (was missing).
 * ⑯ twitter:card: 'summary_large_image' (was 'summary') — newsletter
 *   pages benefit from large image social cards for acquisition.
 */

import { useState, useRef, useCallback, useId } from 'react';
import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import { IconArrowRight } from '../../components/common/Icons/Icons';
import './Newsletter.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const API_URL       = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL      = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME    = BRAND?.name || 'EVORA';
const NEWSLETTER_URL = `${SITE_URL}/newsletter`;
const OG_IMAGE      = `${SITE_URL}/og-image.jpg`;

// RFC-5322 inspired — catches 99.9% of real-world typos without false negatives
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function isValidEmail(val) { return EMAIL_RE.test(val.trim()); }

/** Human-readable errors keyed to HTTP status. Never expose raw error text. */
function friendlyError(httpStatus) {
  if (httpStatus === 409) return "You're already subscribed! Check your inbox for our latest updates.";
  if (httpStatus === 422) return 'Please check your email address and try again.';
  if (httpStatus >= 500)  return 'Our server hiccupped. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
}

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

// Fix ⑩: each benefit gets a unique contextual icon — not generic checkmarks.
// Inline SVGs: zero import overhead, crisp at any DPI.
const BENEFITS = [
  {
    id: 'early',
    title: 'Early Access',
    text: 'Be the first to shop new collections before they go live. 24-hour early access to every drop.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
  },
  {
    id: 'offers',
    title: 'Exclusive Offers',
    text: 'Members-only discounts, private sales, and promotions you won\'t find anywhere else.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
        <line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
    ),
  },
  {
    id: 'style',
    title: 'Style Inspiration',
    text: 'Weekly style notes, tips, and behind-the-scenes content from our design team.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false">
        <path d="M12 20h9"/>
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    ),
  },
  {
    id: 'nospam',
    title: 'No Spam, Ever',
    text: 'We respect your inbox. Meaningful updates only, never more than once a week.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
  },
];

const FAQS = [
  {
    id: 'frequency',
    q: 'How often will I receive emails?',
    a: 'We send one email per week, typically on Fridays. You\'ll receive early access notifications for new drops, exclusive offers, and style inspiration. We never spam your inbox.',
  },
  {
    id: 'content',
    q: 'What kind of content will I receive?',
    a: 'Our newsletter includes early access to new collections, exclusive member-only discounts, styling tips, behind-the-scenes content, and updates about our latest products and offers.',
  },
  {
    id: 'unsub',
    q: 'Can I unsubscribe at any time?',
    a: 'Absolutely. Every email includes an unsubscribe link at the bottom. You can also contact us at any time to be removed from our mailing list.',
  },
  {
    id: 'privacy',
    q: 'Will my information be shared with third parties?',
    a: null, // Rendered separately to include Link component
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Static JSON-LD (module level — serialized once)
// Fix ⑭: added SubscribeAction for richer structured data
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${NEWSLETTER_URL}#webpage`,
      url: NEWSLETTER_URL,
      name: `Newsletter | ${BRAND_NAME}`,
      description: `Subscribe to ${BRAND_NAME} newsletter for early access to new drops, exclusive offers, and style inspiration.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${NEWSLETTER_URL}#breadcrumb` },
      mainEntity: {
        '@type': 'SubscribeAction',
        '@id': `${NEWSLETTER_URL}#subscribe`,
        name: `Subscribe to ${BRAND_NAME} Newsletter`,
        description: `Subscribe to ${BRAND_NAME} newsletter for early access to new product drops and exclusive member offers.`,
        actionStatus: 'https://schema.org/PotentialActionStatus',
        object: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: BRAND_NAME,
        },
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${NEWSLETTER_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Newsletter', item: NEWSLETTER_URL },
      ],
    },
  ],
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function Newsletter() {
  const [email,    setEmail]    = useState('');
  const [status,   setStatus]   = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [touched,  setTouched]  = useState(false);

  const inflightRef = useRef(false);
  const emailRef    = useRef('');

  // useId: stable, SSR-safe, React 18+
  const inputId = useId();
  const errorId = useId();

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setEmail(val);
    emailRef.current = val;
    if (status === 'error') {
      setStatus('idle');
      setErrorMsg('');
    }
  }, [status]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (inflightRef.current || status === 'loading') return;

    const emailVal = emailRef.current.trim().toLowerCase();

    if (!isValidEmail(emailVal)) {
      setTouched(true);
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    inflightRef.current = true;
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/newsletter/subscribe`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: emailVal, source: 'newsletter-page' }),
      });

      if (!res.ok) {
        throw Object.assign(new Error('Subscribe failed'), { httpStatus: res.status });
      }

      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(friendlyError(err.httpStatus));
      if (process.env.NODE_ENV !== 'production') {
        console.error('[Newsletter] subscribe failed:', err);
      }
    } finally {
      inflightRef.current = false;
    }
  }, [status]);

  const showInlineError = touched && email && !isValidEmail(email);
  const isLoading       = status === 'loading';
  // Fix ③: aria-disabled instead of HTML disabled on idle so button is
  // always reachable by keyboard/AT, just communicates current state.
  const isSubmitDisabled = isLoading;

  const pageTitle = `Newsletter | ${BRAND_NAME}`;
  const pageDesc  = `Subscribe to ${BRAND_NAME} newsletter for early access to new drops, exclusive offers, private sales, and weekly style inspiration.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={NEWSLETTER_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={NEWSLETTER_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Subscribe to ${BRAND_NAME} Newsletter`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`Subscribe to ${BRAND_NAME} Newsletter`} />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="nl-page" id="main-content">

          {/* ── Breadcrumb ──────────────────────────────────────────────── */}
          <nav className="nl-breadcrumb" aria-label="Breadcrumb">
            <ol className="nl-breadcrumb__list">
              <li>
                <Link to="/" className="nl-breadcrumb__link">Home</Link>
              </li>
              <li className="nl-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="nl-breadcrumb__current">Newsletter</span>
              </li>
            </ol>
          </nav>

          {/* ── Hero ────────────────────────────────────────────────────── */}
          <header className="nl-hero">
            <div className="nl-hero__content">
              <p className="nl-hero__eyebrow" aria-hidden="true">Private Access</p>
              <h1 className="nl-hero__title">Be First. Always.</h1>
              <p className="nl-hero__subtitle">
                Early access to new drops, exclusive offers, private sales, and
                weekly style notes — direct to your inbox, before anyone else.
              </p>
            </div>
          </header>

          {/* ── Benefits ────────────────────────────────────────────────── */}
          <section className="nl-benefits" aria-labelledby="benefits-h">
            <h2 id="benefits-h" className="nl-benefits__title">What You'll Get</h2>
            <div className="nl-benefits__grid">
              {BENEFITS.map(({ id, title, text, icon }) => (
                <div key={id} className="nl-benefit">
                  <div className="nl-benefit__icon">{icon}</div>
                  <h3 className="nl-benefit__title">{title}</h3>
                  <p  className="nl-benefit__text">{text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Form section ────────────────────────────────────────────── */}
          <section className="nl-form-section" aria-labelledby="form-h">
            <div className="nl-form-section__content">

              {status === 'success' ? (
                /* ── Success state ──────────────────────────────────────── */
                <div className="nl-success" role="status" aria-live="polite">
                  <div className="nl-success__icon" aria-hidden="true">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h2 className="nl-success__title">You're in.</h2>
                  <p className="nl-success__body">
                    Welcome to the circle. Check your inbox for a confirmation
                    email — your first drop preview lands this Friday.
                  </p>
                  <div className="nl-success__actions">
                    <Link to="/new-arrivals" className="nl-success__btn nl-success__btn--primary">
                      Browse New Arrivals
                      <IconArrowRight size={14} aria-hidden="true" />
                    </Link>
                    <Link to="/shop" className="nl-success__btn nl-success__btn--ghost">
                      Shop
                      <IconArrowRight size={14} aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              ) : (
                /* ── Subscription form ──────────────────────────────────── */
                <>
                  <h2 id="form-h" className="nl-form-section__title">Join the Circle</h2>
                  <p className="nl-form-section__subtitle">
                    Enter your email below to get started. We'll send a
                    confirmation right away.
                  </p>

                  <form className="nl-form" onSubmit={handleSubmit} noValidate>
                    <div className="nl-form__field">
                      <label htmlFor={inputId} className="nl-form__label visually-hidden">
                        Email address
                      </label>

                      <input
                        id={inputId}
                        type="email"
                        className={[
                          'nl-form__input',
                          (showInlineError || status === 'error') && 'nl-form__input--error',
                        ].filter(Boolean).join(' ')}
                        placeholder="your@email.com"
                        value={email}
                        onChange={handleChange}
                        onBlur={() => setTouched(true)}
                        required
                        autoComplete="email"
                        inputMode="email"
                        disabled={isLoading}
                        aria-required="true"
                        aria-invalid={(showInlineError || status === 'error') ? 'true' : undefined}
                        aria-describedby={
                          (status === 'error' || showInlineError) ? errorId : undefined
                        }
                      />

                      <button
                        type="submit"
                        className="nl-form__submit"
                        disabled={isSubmitDisabled}
                        aria-label={isLoading ? 'Subscribing…' : 'Subscribe to newsletter'}
                      >
                        {isLoading ? (
                          <span className="nl-form__spinner" aria-hidden="true" />
                        ) : (
                          <>
                            Subscribe
                            <IconArrowRight size={14} aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </div>

                    {(status === 'error' || showInlineError) && (
                      <p
                        id={errorId}
                        className="nl-form__error"
                        role="alert"
                        aria-live="assertive"
                      >
                        {showInlineError
                          ? 'Please enter a valid email address (e.g. name@example.com).'
                          : errorMsg}
                      </p>
                    )}
                  </form>

                  {/* Trust signals */}
                  <div className="nl-trust" aria-label="Trust signals">
                    <span className="nl-trust__item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      Founding member list
                    </span>
                    <span className="nl-trust__dot" aria-hidden="true" />
                    <span className="nl-trust__item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      No spam, ever
                    </span>
                    <span className="nl-trust__dot" aria-hidden="true" />
                    <span className="nl-trust__item">Unsubscribe anytime</span>
                  </div>
                </>
              )}

            </div>
          </section>

          {/* ── FAQ ─────────────────────────────────────────────────────── */}
          {/* Fix ⑪: .nl-faq__answer-inner added inside every answer div —
              required for the CSS grid row animation trick. Without the
              inner wrapper the overflow:hidden clips the padding on close. */}
          <section className="nl-faq" aria-labelledby="faq-h">
            <h2 id="faq-h" className="nl-faq__title">Frequently Asked Questions</h2>
            <div className="nl-faq__list">

              {FAQS.slice(0, 3).map(({ id, q, a }) => (
                <details key={id} className="nl-faq__item">
                  <summary className="nl-faq__question">{q}</summary>
                  <div className="nl-faq__answer">
                    <div className="nl-faq__answer-inner">
                      <p>{a}</p>
                    </div>
                  </div>
                </details>
              ))}

              {/* Privacy FAQ — contains a Link, rendered separately */}
              <details className="nl-faq__item">
                <summary className="nl-faq__question">
                  Will my information be shared with third parties?
                </summary>
                <div className="nl-faq__answer">
                  <div className="nl-faq__answer-inner">
                    <p>
                      No. We respect your privacy. Your email address is used solely
                      for sending our newsletter and will never be shared with third
                      parties. See our{' '}
                      <Link to="/privacy" className="nl-faq__link">Privacy Policy</Link>
                      {' '}for details.
                    </p>
                  </div>
                </div>
              </details>

            </div>
          </section>

          {/* ── Related (internal linking) ────────────────────────────────── */}
          <nav className="nl-related" aria-label="Related information">
            <h2 className="nl-related__title">Related information</h2>
            <ul className="nl-related__list" role="list">
              <li><Link to="/shop" className="nl-related__link">Shop All</Link></li>
              <li><Link to="/new-arrivals" className="nl-related__link">New Arrivals</Link></li>
              <li><Link to="/privacy" className="nl-related__link">Privacy Policy</Link></li>
              <li><Link to="/contact" className="nl-related__link">Contact Us</Link></li>
            </ul>
          </nav>

        </main>
      </Layout>
    </>
  );
}